import React, { useState } from "react";
import { useAuth } from "../../AuthContext";
import { api, formatErr } from "../../api";
import { SectionTitle, Avatar } from "../../components/Atoms";
import { Gear, SignOut, ArrowsClockwise, WarningCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const reseed = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/reseed");
      setResult(data);
      toast.success("Demo data refreshed");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
      setConfirming(false);
    }
  };

  return (
    <div className="space-y-5">
      <SectionTitle title="Settings" subtitle="Your account, your control room" />
      <div className="glass p-6">
        <div className="flex items-center gap-4">
          <Avatar name={user?.name} size={64} />
          <div>
            <div className="text-2xl font-black text-white">{user?.name}</div>
            <div className="text-sm text-muted">{user?.email}</div>
            <div className="pill completed mt-2 inline-block">{user?.role}</div>
          </div>
        </div>
      </div>

      <div className="glass p-6">
        <div className="text-white font-bold mb-3 flex items-center gap-2"><Gear size={18} weight="bold" /> Preferences</div>
        <div className="text-sm text-muted">Theme is locked to dark neon for the demo. More controls coming soon.</div>
      </div>

      {user?.role === "admin" && (
        <div className="glass p-6">
          <div className="text-white font-bold mb-1 flex items-center gap-2"><ArrowsClockwise size={18} weight="bold" /> Demo Data</div>
          <div className="text-xs text-muted mb-3">Wipe all clients, ambassadors, projects, tasks, applications, submissions and payouts — then reseed rich demo data. Use this before a presentation.</div>
          <button
            onClick={() => setConfirming(true)}
            className="btn-ghost text-sm text-[#F59E0B] flex items-center gap-2"
            data-testid="reseed-demo-btn"
          >
            <ArrowsClockwise size={16} weight="bold" /> Reset demo data
          </button>
          {result && (
            <div className="mt-4 text-xs text-muted bg-white/5 rounded-2xl p-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="reseed-result">
              {["clients","ambassadors","projects","tasks","applications","submissions","payouts"].map(k => (
                <div key={k} className="flex justify-between">
                  <span className="capitalize">{k}</span>
                  <span className="text-white font-bold">{result[k]}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <button onClick={logout} className="btn-ghost flex items-center justify-center gap-2 w-full sm:w-auto" data-testid="settings-logout-btn"><SignOut size={16} weight="bold" /> Sign out</button>

      {confirming && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => !loading && setConfirming(false)}>
          <div className="glass p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2 text-[#F59E0B] font-bold"><WarningCircle size={20} weight="fill" /> Reset demo data?</div>
              {!loading && <button onClick={() => setConfirming(false)} className="text-muted hover:text-white" data-testid="reseed-cancel-x"><X size={18} /></button>}
            </div>
            <div className="text-sm text-muted mt-2">This will <span className="text-[#EC4899] font-semibold">erase</span> all clients, ambassadors, projects, tasks, applications, submissions and payouts in this environment, then reseed fresh demo data. Demo logins (admin / brand / creator) will keep working. This cannot be undone.</div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setConfirming(false)} disabled={loading} className="btn-ghost flex-1 text-sm" data-testid="reseed-cancel-btn">Cancel</button>
              <button onClick={reseed} disabled={loading} className="btn-primary flex-1 text-sm flex items-center justify-center gap-2" data-testid="reseed-confirm-btn">
                {loading ? "Reseeding…" : <><ArrowsClockwise size={14} weight="bold" /> Reset</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
